import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Calendar, BookOpen, Heart, Target, Quote } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';
import aboutPhoto from '../assets/about.png';


const AboutSection = () => {
  const { aboutData } = useLanguage();
  const icons = [Award, Users, Calendar, BookOpen];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section 
      id="about" 
      className="py-20 md:py-32 relative overflow-hidden"
      style={{ background: 'var(--bg-page)' }}
    >
      <div className="container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="mono-text mb-4 block" style={{ color: 'var(--accent-gold-400)' }}>
            CONHEÇA
          </span>
          <h2 className="heading-hero mb-4">{aboutData.title}</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            {aboutData.subtitle}
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-8 items-start">
          {/* Left Column - Photo */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 relative"
          >
            <div className="relative">
              {/* Fundo dourado decorativo */}
              <div
                className="absolute -top-4 -left-4 w-full h-full rounded-2xl"
                style={{ background: 'var(--accent-gold-200)' }}
              />

              {/* Card com foto dentro */}
              <div className="rounded-2xl overflow-hidden relative" style={{ background: 'var(--bg-card)', height: '580px' }}>
                <motion.img
                  src={aboutPhoto}
                  alt="Lucimar Bizoni"
                  whileHover={{ scale: 1.04 }}
                  transition={{ duration: 0.4 }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
                    maskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)'
                  }}
                />
              </div>

              {/* Quote Card - Floating */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="absolute -bottom-6 -right-4 md:-right-8 voice-card shadow-xl max-w-[260px]"
                style={{ background: 'var(--bg-card)', zIndex: 20 }}
              >
                <Quote size={20} className="mb-2" style={{ color: 'var(--accent-gold-400)' }} />
                <p className="body-small italic" style={{ color: 'var(--text-secondary)' }}>
                  "Minha missão é orientar, inspirar e transformar vidas pelos princípios bíblicos."
                </p>
                <p className="caption mt-2 font-medium">— Lucimar Bizoni</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column - Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:col-span-3"
          >
            {/* Bio */}
            <div className="space-y-4 mb-8">
              {aboutData.bio.map((paragraph, index) => (
                <motion.p 
                  key={index}
                  variants={itemVariants}
                  className={index === 0 ? "body-large" : "body-medium"}
                  style={{ color: index === 0 ? 'var(--text-primary)' : 'var(--text-secondary)' }}
                >
                  {paragraph}
                </motion.p>
              ))}
            </div>

            {/* Experience Stats - Elegant Cards */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-2 gap-3 mb-8"
            >
              {aboutData.experience.map((stat, index) => {
                const IconComponent = icons[index];
                const accents = ['gold', 'bronze', 'rose', 'green'];
                return (
                  <div
                    key={index}
                    className={`voice-card accent-${accents[index]} p-4`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <IconComponent size={18} style={{ color: 'var(--text-primary)' }} />
                      <span className="heading-2" style={{ fontSize: '1.1rem' }}>{stat.title}</span>
                    </div>
                    <p className="caption">{stat.description}</p>
                  </div>
                );
              })}
            </motion.div>

            {/* Roles List */}
            <motion.div variants={itemVariants} className="space-y-2 mb-8">
              {aboutData.roles.slice(0, 4).map((role, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div 
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: 'var(--accent-gold-200)' }}
                  >
                    <Heart size={10} style={{ color: 'var(--accent-gold-400)' }} />
                  </div>
                  <p className="body-small">{role}</p>
                </div>
              ))}
            </motion.div>

            {/* Mission Card */}
            <motion.div
              variants={itemVariants}
              className="p-6 rounded-xl"
              style={{ background: 'var(--accent-sage-200)', border: '1px solid var(--border-light)' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--bg-card)' }}
                >
                  <Target size={18} style={{ color: 'var(--text-primary)' }} />
                </div>
                <h3 className="heading-3">Missão</h3>
              </div>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                {aboutData.mission}
              </p>
            </motion.div>

            {/* Values Tags */}
            <motion.div
              variants={itemVariants}
              className="mt-6 flex flex-wrap gap-2"
            >
              {['Fé', 'Propósito', 'Transformação', 'Excelência', 'Amor'].map((value) => (
                <span key={value} className="btn-tag">
                  {value}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
